import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { type HeadData, setHeadData } from 'utils/metaDataUtils/headData';
import type { Head } from 'interfaces/Head';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { setPianoConfig } from 'services/Piano';
import type { HeaderNav } from 'interfaces/navigation/Header';
import AuthorPage from 'pages/Author/AuthorPage';
import type { PageViewDataLayerObject } from 'services/Gtm/entities';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import getAuthorPageBySlug from 'api/Author/getAuthorPageBySlug';
import type { PageInfo } from 'interfaces/components/PageInfo';
import { layoutCount } from 'constants/constants';
import { authorSlugs } from 'lib/mockData/Author/author-slugs';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import type { PageAdConfig } from 'interfaces/ads/Ad';
import setAuthorPageAdConfig from 'services/Ad/config/setAuthorPageAdConfig';
import getFooter from 'api/Footer/getFooter';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getMenu from 'api/Header/getMenu';
import { calculatePagePagination, type PagePagination } from 'utils/miscUtils/calculatePageInfo';
import type { AuthorPageData } from 'interfaces/content/AuthorPageData';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';

interface AuthorProps {
  author: AuthorPageData;
  allPostsArray: ArticlePreview[];
  authorPageInfo: PageInfo;
  name: string;
  pageSize: number;
  pagination: PagePagination;
}

const AuthorNextPage: NextPage<AuthorProps> = ({
  pagination,
  author,
  allPostsArray,
  authorPageInfo,
  name,
  pageSize,
}: AuthorProps) => (
  <AuthorPage
    pagination={pagination}
    author={author}
    allPostsArray={allPostsArray}
    authorPageInfo={authorPageInfo}
    pageNum='1'
    name={name}
    pageSize={pageSize}
  />
);

export default AuthorNextPage;

export const getStaticProps: GetStaticProps = async (context) => {
  const pageSize = layoutCount.author;

  try {
    const { name } = context.params as { name: string };

    const { pageNum } = (context.params || {}) as { pageNum: string };

    const currentPageNum = parseInt(pageNum || '1', 10);

    const offset = (currentPageNum - 1) * pageSize;
    const { author, authorPageInfo, allPostsArray } = await getAuthorPageBySlug(name, pageSize, offset);

    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);

    const dataLayer: PageViewDataLayerObject = safe(() => setPageGtmConfig(author), fallbackData.dataLayer);
    const pagination = calculatePagePagination(currentPageNum, authorPageInfo.totalCount, layoutCount.latest, 'all');
    const canonicalUrl = pageNum ? `${author.canonicalUrl}page/${pageNum}/` : author.canonicalUrl;

    const headPayload: Head = {
      ...author,
      name: author.displayName,
      slug: author.authorSlug,
      link: canonicalUrl,
      pageInfo: authorPageInfo,
      posts: allPostsArray,
      canonicalUrl,
      titlePrefix: pagination.pageInfo,
    };

    const headData: HeadData = safe(() => setHeadData(headPayload, true, 'Fortune'), fallbackData.headData);

    const arcSpanPageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(author),
      fallbackData.arcSpanPageConfig,
    );
    const pianoPageConfig: PianoPageConfig = safe(() => setPianoConfig(author), fallbackData.pianoPageConfig);
    const pageAdConfig: PageAdConfig = safe(() => setAuthorPageAdConfig(author), fallbackData.pageAdConfig);

    return {
      props: {
        allPostsArray,
        arcSpanPageConfig,
        author,
        authorPageInfo,
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        name,
        pageAdConfig,
        pageSize,
        pagination,
        pianoPageConfig,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<{ name: string }> = () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: {
            name: 'sophie-mellor',
          },
        },
      ],
    };
  }

  const paths = authorSlugs.map((name) => ({
    params: {
      name,
    },
  }));
  return {
    fallback: 'blocking',
    paths,
  };
};
